import { Plus, Trash2, BedDouble } from "lucide-react";
import { formatCurrencyFull } from "@/lib/data";
import { RoomBookingItem } from "./types";

interface RoomBookingsSectionProps {
  eventId: number;
  rooms: RoomBookingItem[];
  onAdd: () => void;
  onDelete: (id: number) => void;
}

const STATUS_COLORS: Record<string, string> = {
  booked:      "bg-amber-100 text-amber-700",
  checked_in:  "bg-blue-100 text-blue-700",
  checked_out: "bg-emerald-100 text-emerald-700",
  cancelled:   "bg-red-100 text-red-600",
};

export default function RoomBookingsSection({ rooms, onAdd, onDelete }: RoomBookingsSectionProps) {
  const totalCharges = (rooms || []).reduce((sum, r) => sum + (r.amount || 0), 0);

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-sm">
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-stone-50 to-stone-100 border-b border-stone-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
            <BedDouble className="w-4 h-4 text-amber-600" />
          </div>
          <div>
            <h3 className="font-bold text-stone-800 text-sm">Room Bookings</h3>
            <p className="text-[10px] text-stone-500">{rooms?.length || 0} room(s) booked</p>
          </div>
        </div>
        <button
          onClick={onAdd}
          className="text-xs px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-md font-medium transition-colors flex items-center gap-1"
        >
          <Plus className="w-3 h-3" />
          Add Room
        </button>
      </div>

      {/* Table */}
      {rooms && rooms.length > 0 ? (
        <>
          {/* Desktop */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-100 bg-stone-50/50">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">Room</th>
                  <th className="text-left px-3 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">Type</th>
                  <th className="text-left px-3 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">Check-in</th>
                  <th className="text-left px-3 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">Check-out</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">Nights</th>
                  <th className="text-right px-3 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">Rate/Night</th>
                  <th className="text-right px-3 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">Total</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wider">Status</th>
                  <th className="px-3 py-2.5 w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-50">
                {rooms.map((room) => (
                  <tr key={room.id} className="hover:bg-stone-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-medium text-stone-800 text-sm">{room.name}</p>
                      {room.guest_name && (
                        <p className="text-xs text-stone-400">{room.guest_name}</p>
                      )}
                    </td>
                    <td className="px-3 py-3 text-xs text-stone-500">{room.type || "—"}</td>
                    <td className="px-3 py-3 text-xs text-stone-600">{room.check_in}</td>
                    <td className="px-3 py-3 text-xs text-stone-600">{room.check_out}</td>
                    <td className="px-3 py-3 text-center text-xs font-medium text-stone-700">{room.days}</td>
                    <td className="px-3 py-3 text-right text-xs text-stone-600">{formatCurrencyFull(room.rate)}</td>
                    <td className="px-3 py-3 text-right font-bold text-stone-900 text-sm">{formatCurrencyFull(room.amount)}</td>
                    <td className="px-3 py-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize ${STATUS_COLORS[room.status] || "bg-stone-100 text-stone-600"}`}>
                        {room.status.replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <button
                        onClick={() => onDelete(room.id)}
                        className="p-1.5 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        title="Remove room booking"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile */}
          <div className="sm:hidden divide-y divide-stone-100">
            {rooms.map((room) => (
              <div key={room.id} className="px-4 py-3 flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-stone-800 text-sm truncate">{room.name}</p>
                  <p className="text-xs text-stone-500 mt-0.5">{room.check_in} → {room.check_out} · {room.days} night(s)</p>
                  <p className="text-xs text-stone-500">{formatCurrencyFull(room.rate)}/night</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-bold text-stone-900 text-sm">{formatCurrencyFull(room.amount)}</p>
                  <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize ${STATUS_COLORS[room.status] || "bg-stone-100 text-stone-600"}`}>
                    {room.status.replace("_", " ")}
                  </span>
                </div>
                <button
                  onClick={() => onDelete(room.id)}
                  className="p-1.5 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors flex-shrink-0 mt-0.5"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>

          {/* Footer total */}
          <div className="px-4 py-3 bg-amber-50 border-t border-amber-100 flex items-center justify-between">
            <span className="text-xs font-semibold text-amber-700 uppercase tracking-wider">Total Room Charges</span>
            <span className="text-base font-bold text-amber-800">{formatCurrencyFull(totalCharges)}</span>
          </div>
        </>
      ) : (
        <div className="px-4 py-8 text-center">
          <BedDouble className="w-8 h-8 text-stone-300 mx-auto mb-2" />
          <p className="text-sm text-stone-400">No rooms booked for this event</p>
          <button
            onClick={onAdd}
            className="mt-3 text-xs px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-md font-medium transition-colors"
          >
            + Book a Room
          </button>
        </div>
      )}
    </div>
  );
}
